import React, { useEffect, useId, useRef, useState } from 'react';
import { FiCheck, FiMusic, FiPlay, FiRadio, FiSliders, FiX } from 'react-icons/fi';
import {
  getTileSurfaceStyle,
  resolveTileColor,
  sanitizeTileColor,
  tileDisplayTitle,
} from './audioTileAppearance';

export default function AudioLandingTile({
  variant = 'playlist',
  genreId = 'playlist',
  defaultTitle = '',
  tileLabel = null,
  tileColor = null,
  meta = '',
  badge = '',
  onActivate,
  onSaveAppearance = null,
  customizeLabel = 'Customize tile',
}) {
  const popoverId = useId();
  const wrapRef = useRef(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [draftLabel, setDraftLabel] = useState('');
  const [draftColor, setDraftColor] = useState('');
  const [saving, setSaving] = useState(false);

  const displayTitle = tileDisplayTitle(defaultTitle, tileLabel);
  const accent = resolveTileColor(tileColor, genreId);
  const surfaceStyle = getTileSurfaceStyle(accent);

  useEffect(() => {
    if (!customOpen) return undefined;
    setDraftLabel(tileLabel || defaultTitle || '');
    setDraftColor(sanitizeTileColor(tileColor) || accent);
  }, [customOpen, tileLabel, defaultTitle, tileColor, accent]);

  useEffect(() => {
    if (!customOpen) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setCustomOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [customOpen]);

  const Icon = variant === 'station' ? FiRadio : FiMusic;

  const handleSaveCustomize = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!onSaveAppearance) {
      setCustomOpen(false);
      return;
    }
    setSaving(true);
    try {
      await onSaveAppearance({
        tileLabel: draftLabel.trim(),
        tileColor: sanitizeTileColor(draftColor),
      });
      setCustomOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const handleClearColor = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDraftColor('');
  };

  return (
    <div className="audio-player-landing__tile-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`audio-player-landing__tile audio-player-landing__tile--${variant}`}
        data-genre={genreId}
        style={surfaceStyle}
        onClick={() => onActivate?.()}
      >
        <span className="audio-player-landing__tile-icon" aria-hidden>
          <Icon />
        </span>
        <span className="audio-player-landing__tile-title">{displayTitle}</span>
        {meta ? <span className="audio-player-landing__tile-meta">{meta}</span> : null}
        {badge ? <span className="audio-player-landing__tile-badge">{badge}</span> : null}
        {variant === 'station' ? (
          <span className="audio-player-landing__tile-play" aria-hidden>
            <FiPlay />
          </span>
        ) : null}
        {onSaveAppearance ? (
          <span
            className="audio-player-landing__tile-customize-hit"
            role="presentation"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="audio-player-landing__tile-customize"
              aria-label={customizeLabel}
              aria-expanded={customOpen}
              aria-controls={popoverId}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCustomOpen((v) => !v);
              }}
            >
              <FiSliders aria-hidden />
            </button>
          </span>
        ) : null}
      </button>

      {customOpen && onSaveAppearance ? (
        <form
          id={popoverId}
          className="audio-player-landing__tile-popover"
          style={getTileSurfaceStyle(sanitizeTileColor(draftColor) || accent)}
          onSubmit={handleSaveCustomize}
          onClick={(e) => e.stopPropagation()}
        >
          <p className="audio-player-landing__tile-popover-title">Tile appearance</p>
          <label className="audio-player-landing__tile-popover-field">
            <span>Title</span>
            <input
              type="text"
              value={draftLabel}
              onChange={(ev) => setDraftLabel(ev.target.value)}
              maxLength={120}
              placeholder={defaultTitle}
            />
          </label>
          <label className="audio-player-landing__tile-popover-field audio-player-landing__tile-popover-field--color">
            <span>Colour</span>
            <input
              type="color"
              value={sanitizeTileColor(draftColor) || accent}
              onChange={(ev) => setDraftColor(ev.target.value)}
              aria-label="Tile colour"
            />
            <button type="button" className="audio-player-landing__tile-popover-reset" onClick={handleClearColor}>
              Default
            </button>
          </label>
          <div className="audio-player-landing__tile-popover-actions">
            <button type="submit" disabled={saving}>
              <FiCheck aria-hidden /> {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => setCustomOpen(false)}>
              <FiX aria-hidden /> Cancel
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
